import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
  useReducedMotion,
} from "framer-motion";
import { HelpCircle, Trophy, Zap, Brain, ArrowDown } from "lucide-react";
import { AppStoreBadge } from "@/components/AppStoreBadge";
import { GooglePlayBadge } from "@/components/GooglePlayBadge";
import { ChunkyButton } from "@/components/ChunkyButton";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const FloatCard = ({ springX, springY, depth, className, children, floatDelay = 0 }) => {
  const x = useTransform(springX, (v) => v * depth);
  const y = useTransform(springY, (v) => v * depth);
  return (
    <motion.div
      style={{ x, y }}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7, delay: 1 + floatDelay, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute hidden lg:block ${className}`}
    >
      <div className="animate-float-soft" style={{ animationDelay: `${floatDelay}s` }}>
        {children}
      </div>
    </motion.div>
  );
};

const CardShell = ({ children, color = "bg-white" }) => (
  <div
    className={`flex items-center gap-3 rounded-[12px] border-2 border-[#1A1D1E] ${color} px-4 py-3 shadow-[0_6px_0_0_#1A1D1E]`}
  >
    {children}
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const springX = useSpring(mx, { stiffness: 55, damping: 16 });
  const springY = useSpring(my, { stiffness: 55, damping: 16 });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const handleMouse = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={handleMouse}
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <motion.div style={{ y: blobY }} aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#1FBCA2]/15 blur-3xl" />
        <div className="absolute top-1/3 -right-32 h-[28rem] w-[28rem] rounded-full bg-[#FFC837]/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-[#FF7B72]/15 blur-3xl" />
      </motion.div>

      <FloatCard springX={springX} springY={springY} depth={-40} className="left-[6%] top-[18%] -rotate-6" floatDelay={0.1}>
        <CardShell color="bg-[#FFC837]">
          <HelpCircle className="h-6 w-6" />
          <span className="font-display font-semibold">Q: Biggest planet?</span>
        </CardShell>
      </FloatCard>
      <FloatCard springX={springX} springY={springY} depth={55} className="right-[5%] top-[16%] rotate-6" floatDelay={0.35}>
        <CardShell>
          <Trophy className="h-6 w-6 text-[#1FBCA2]" />
          <span className="font-display font-semibold">Team Grandma wins!</span>
        </CardShell>
      </FloatCard>
      <FloatCard springX={springX} springY={springY} depth={-65} className="bottom-[14%] left-[10%] rotate-3" floatDelay={0.6}>
        <CardShell color="bg-[#FF7B72]">
          <Zap className="h-6 w-6 fill-[#1A1D1E]" />
          <span className="font-display font-semibold">Lightning round!</span>
        </CardShell>
      </FloatCard>
      <FloatCard springX={springX} springY={springY} depth={40} className="bottom-[20%] right-[8%] -rotate-3" floatDelay={0.85}>
        <CardShell color="bg-[#1FBCA2]">
          <Brain className="h-6 w-6" />
          <span className="font-display font-semibold">A: Jupiter</span>
        </CardShell>
      </FloatCard>

      <motion.div
        style={{ y: textY }}
        className="relative mx-auto flex max-w-5xl flex-col items-center px-6 pb-28 pt-20 text-center md:pt-28"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          data-testid="hero-eyebrow"
          className="mb-8 inline-flex items-center gap-2 rounded-full border-2 border-[#1A1D1E] bg-white px-5 py-2 text-xs font-extrabold uppercase tracking-[0.2em] shadow-[0_4px_0_0_#1A1D1E]"
        >
          <span className="h-2.5 w-2.5 rounded-full bg-[#1FBCA2]" />
          Ages 6+ · Fair-play family trivia
        </motion.div>

        <h1 className="font-display text-5xl font-bold leading-[1.02] tracking-tight md:text-7xl lg:text-8xl">
          <MaskedLine delay={0.15}>Big laughs,</MaskedLine>
          <MaskedLine delay={0.3}>
            <span className="relative inline-block">
              real learning,
              <motion.svg
                viewBox="0 0 320 16"
                className="absolute -bottom-5 left-0 w-full"
                aria-hidden="true"
              >
                <motion.path
                  d="M6 12 C 80 3, 230 3, 314 9"
                  stroke="#FFC837"
                  strokeWidth="7"
                  strokeLinecap="round"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.7, delay: 1.05, ease: "easeOut" }}
                />
              </motion.svg>
            </span>
          </MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="text-[#1FBCA2]">zero ads.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-tagline"
          className="mt-8 max-w-xl text-base leading-relaxed text-[#6B7280] md:text-lg"
        >
          TriviaPro is fair-play family trivia for kids and adults. Clever
          questions, sneaky-fun twists, and rules that keep the peace – from
          curious six-year-olds to competitive grandparents.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <AppStoreBadge testId="hero-app-store-badge" />
          <GooglePlayBadge testId="hero-google-play-badge" />
          <ChunkyButton href="#how-it-works" color="white" testId="hero-how-it-works-button">
            Why it's different
            <ArrowDown className="h-4 w-4" />
          </ChunkyButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          data-testid="hero-stats"
          className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-extrabold uppercase tracking-widest text-[#1A1D1E]/60"
        >
          <span>500+ family-safe questions</span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#FF7B72]" />
          <span>2–8 players</span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#FFC837]" />
          <span>No ads, no data collection</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
